/** @format */

import moment from 'moment';
import { sortBy } from '../../../../utils/sort';
import { normalizeGroup } from './groupSchema';
import { planningTimeFromApi } from './technicianUnavailability';

import { PLANNING_DEFAULT_START } from './constants';
import { normalizePlanningStartConfig } from './planningStartHours';
import { interventionClientHurrySortRank } from './domainColors';
import { workItemFromApi } from './planningWorkItems';

export { PLANNING_DEFAULT_START } from './constants';

function soldProductsFromApi(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const lines = [];
  (raw.lines || []).forEach((productLine) => {
    lines.push(productLine);
    if (productLine.kit_lines?.length) {
      productLine.kit_lines.forEach((kitLine) => {
        lines.push({
          ...kitLine,
          is_kit_line: true,
        });
      });
    }
  });
  return {
    isV1: Boolean(raw.is_v1),
    lines: [...lines].sort(sortBy('order')),
  };
}

/** Singleton planning parameters from GET/PATCH /planning/config/. */
export function planningConfigFromApi(data) {
  const raw =
    data?.default_start ?? data?.default_start_time ?? PLANNING_DEFAULT_START;
  return normalizePlanningStartConfig({
    id: data?.id ?? 1,
    defaultStart: planningTimeFromApi(raw) || PLANNING_DEFAULT_START,
    seasonalEnabled: data?.seasonal_start_enabled,
    periods: data?.seasonal_periods,
  });
}

function confirmationEventsFromApi(entries) {
  if (!Array.isArray(entries)) return [];
  return entries.map((entry) => ({
    id: entry.id,
    type: entry.type || '',
    createdDate: entry.created_date || null,
    comment: entry.comment || '',
    userName: entry.user_name || '',
    body: entry.body || '',
    sent: entry.sent !== false,
  }));
}

export function tripLogFromApi(data) {
  return {
    id: data.id,
    type: data.type || '',
    typeLabel: data.type_label || '',
    createdDate: data.created_date || null,
    comment: data.comment || '',
    userName: data.user_name || '',
    tripId: data.trip_id ?? null,
    tripPk: data.trip_pk ?? null,
    tripLabel: data.trip_label || '',
    siteId: data.site_id ?? null,
    siteLabel: data.site_label || '',
  };
}

function confirmationStatusFromApi(raw) {
  if (!raw || typeof raw !== 'object') {
    return {
      status: 'not_asked',
      comment: '',
      hasBeenForced: false,
      link: '',
      arrivalTimeDisplay: 'fixed',
      confirmationDate: null,
      refusalDate: null,
      sentSmsIds: [],
      latestSentDate: null,
      events: [],
    };
  }
  return {
    status: raw.status || 'not_asked',
    comment: raw.comment || '',
    hasBeenForced: Boolean(raw.has_been_forced),
    link: raw.link || '',
    arrivalTimeDisplay: raw.arrival_time_display || 'fixed',
    confirmationDate: raw.confirmation_date || null,
    refusalDate: raw.refusal_date || null,
    sentSmsIds: raw.sent_sms_ids || [],
    latestSentDate: raw.latest_sent_date || null,
    events: confirmationEventsFromApi(raw.events),
  };
}

function worksheetAnswersFromApi(rows) {
  if (!Array.isArray(rows)) return [];
  return rows
    .filter((row) => row && typeof row === 'object')
    .map((row) => ({
      worksheetId: row.worksheet_id ?? null,
      worksheetName: row.worksheet_name || '',
      questionId: row.question_id ?? null,
      questionType: row.question_type || '',
      answerId: row.answer_id ?? null,
      question: row.question || '',
      answer: row.answer || '',
      photoUrl: row.photo_url || row.drive_file?.thumbnail_url || '',
      driveFile: row.drive_file ? driveFileFromApi(row.drive_file) : null,
    }))
    .filter((row) => row.question);
}

/** Trip row used across the planning UI (déplacement). */
export function tripFromApi(data) {
  const estimatedHours = Number(data.estimated_hours ?? data.h) || 0;
  return {
    id: data.id,
    createdDate: data.created_date || null,
    externalId: data.external_id ?? data.id,
    interventionId: data.intervention_id ?? null,
    projectId: data.project_id ?? null,
    chantierId: data.project_id ?? null,
    domain: data.domain || '',
    serviceTypeSlug: data.service_type_slug || '',
    interventionIsVerified: Boolean(data.intervention_is_verified),
    interventionNeedToBeFollowedUp: Boolean(
      data.intervention_need_to_be_followed_up,
    ),
    label: data.label || '',
    estimatedHours,
    note: data.note || '',
    date: data.date || '',
    groups: (data.groups || []).map((group, index) =>
      normalizeGroup(group, estimatedHours, index),
    ),
    constraints: (data.constraints || []).map((row) => ({
      id: row.id,
      constraintId: row.constraint_id,
      constraintLabel: row.constraint_label || '',
      statusId: row.status_id,
      statusLabel: row.status_label || '',
    })),
    competences: data.competences || [],
    not_planned: data.not_planned !== false,
    pre_planned: Boolean(data.pre_planned),
    planned: Boolean(data.planned),
    done: Boolean(data.done),
    syncedWithBigchange: Boolean(data.synced_with_bigchange),
    bigchangeSyncSnapshot: data.bigchange_sync_snapshot || null,
    clientAvailabilityForced: Boolean(data.client_availability_forced),
    isImportedFromBigchange: Boolean(data.is_imported_from_bigchange),
    worksheetAnswers: worksheetAnswersFromApi(data.worksheet_answers),
    isLocked: Boolean(data.is_locked),
    isFinalisation: Boolean(data.is_finalisation),
    difficulty: [1, 2, 3].includes(Number(data.difficulty))
      ? Number(data.difficulty)
      : null,
    lockReason: data.lock_reason || '',
    updatedDate: data.updated_date || null,
    version: Number(data.version) > 0 ? Number(data.version) : 0,
    updatedByName: data.updated_by_name || '',
    confirmationStatus: confirmationStatusFromApi(data.confirmation_status),
    priorityId: data.priority_id ?? null,
    extraLow: Boolean(data.extra_low),
    low: Boolean(data.low),
    medium: data.medium !== false,
    high: Boolean(data.high),
    extraHigh: Boolean(data.extra_high),
    priorityColor: (data.color || '').trim(),
    sourceTripId: data.source_trip_id ?? null,
    duplicatedTripId: data.duplicated_trip_id ?? null,
    isAlternateSlotSourceGhost: Boolean(data.is_alternate_slot_source_ghost),
  };
}

function confirmableTripFromApi(row) {
  return {
    id: row.id,
    externalId: row.external_id,
    label: row.label || '',
    date: row.date || null,
    dateLabel: row.date_label || '',
    timeLabel: row.time_label || '',
    confirmationStatus: row.confirmation_status || 'not_asked',
  };
}

export function tripConfirmationDetailFromApi(raw) {
  const contact = raw.contact || {};
  return {
    ...confirmationStatusFromApi(raw),
    defaultSmsBody: raw.default_sms_body || '',
    defaultEmailSubject: raw.default_email_subject || '',
    defaultEmailBody: raw.default_email_body || '',
    contact: {
      email: contact.email || '',
      emailCc: contact.email_cc || '',
      defaultRecipients: contact.default_recipients || [],
      defaultCc: contact.default_cc || [],
      defaultSmsRecipients: contact.default_sms_recipients || [],
    },
    confirmableTrips: (raw.confirmable_trips || []).map(confirmableTripFromApi),
    bundledTripIds: raw.bundled_trip_ids || [],
    anchorTripId: raw.anchor_trip_id ?? null,
  };
}

/** Trip (déplacement) marked as finished in InterventionStatus. */
export function isTripDone(trip) {
  return Boolean(
    trip?.date && Boolean(moment(trip.date).isBefore(moment().startOf('day'))),
  );
}

/** Sidebar / queue ordering: urgent clients first, then newest interventions. */
export function sortSidebarQueueItems(rows) {
  return [...rows].sort((a, b) => {
    const aRank = interventionClientHurrySortRank(a.intervention);
    const bRank = interventionClientHurrySortRank(b.intervention);
    if (aRank !== bRank) return aRank - bRank;
    return Number(b.intervention?.id || 0) - Number(a.intervention?.id || 0);
  });
}

/** @deprecated Use tripFromApi */
export const interventionFromApi = tripFromApi;

/** Prestation tag chip (auto from sold products / qualification, or manual). */
export function interventionTagFromApi(data) {
  return {
    id: data.id,
    code: data.code || '',
    label: data.label || '',
    color: data.color || 'default',
    icon: data.icon || '',
    position: data.position ?? 0,
    source: data.source || 'auto',
    isManual: data.source === 'manual',
  };
}

/** Intervention layer row (typed: chantier / VT / SAV) carrying nested trips. */
export function interventionLayerFromApi(
  data,
  { includeImportedTrips = false } = {},
) {
  const client = data.client
    ? {
        id: data.client.id ?? null,
        ref:
          data.client.ref ||
          (data.client.id != null ? String(data.client.id) : ''),
        mat: data.client.mat || '',
        pv: Boolean(data.client.pv),
        bat: Boolean(data.client.bat),
        aa: Boolean(data.client.aa),
        ae: Boolean(data.client.ae),
        ev: Boolean(data.client.ev),
        hw: Boolean(data.client.hw),
        th: Boolean(data.client.th),
        availabilityQuestionnaire:
          data.client.availability_questionnaire ?? null,
        advancePaid:
          typeof data.client.advance_paid === 'boolean'
            ? data.client.advance_paid
            : null,
        beginConstructionDate: data.client.begin_construction_date || null,
        endConstructionDate: data.client.end_construction_date || null,
        constructionTechnicians: (
          data.client.construction_technicians || []
        ).map((technician) => ({
          id: technician.id,
          firstName: technician.first_name || '',
          fullName: technician.full_name || technician.first_name || '',
        })),
        planningStatus: data.client.planning_status || null,
        planningStatusIsToBePlanned: Boolean(
          data.client.planning_status_is_to_be_planned,
        ),
        planningStatusIsPrePlanned: Boolean(
          data.client.planning_status_is_pre_planned,
        ),
        planningStatusIsWaiting: Boolean(
          data.client.planning_status_is_waiting,
        ),
        planningStatusIsOk: Boolean(data.client.planning_status_is_ok),
        planningStatusIsAbandoned: Boolean(
          data.client.planning_status_is_abandoned,
        ),
        advancePaidDate: data.client.advance_paid_date || null,
        soldProducts: soldProductsFromApi(data.client.sold_products),
        workforceQualification: data.client.workforce_qualification
          ? {
              rows: (data.client.workforce_qualification.rows || []).map(
                (row) => ({
                  key: row.key,
                  label: row.label,
                  value: row.value,
                  source: row.source,
                }),
              ),
            }
          : null,
      }
    : null;
  return {
    id: data.id,
    createdDate: data.created_date || null,
    updatedDate: data.updated_date || null,
    version: Number(data.version) > 0 ? Number(data.version) : 0,
    daysPending: data.days_pending != null ? Number(data.days_pending) : null,
    // Chantier id is now the LeadSite id; backend still sends it as project_id (alias).
    projectId: data.site_id ?? data.project_id ?? null,
    domainId: data.domain_id ?? null,
    domainLabel: data.domain_label || '',
    serviceTypeId: data.service_type_id ?? null,
    serviceTypeLabel: data.service_type_label || '',
    serviceTypeSlug: data.service_type_slug || '',
    label: data.label || '',
    description: data.description || '',
    subject: data.subject || '',
    siteId: data.site_id ?? null,
    clientId: data.client_id ?? data.client_project_id ?? null,
    googleFolderId: data.google_folder_id || '',
    googleDriveUrl: data.google_drive_url || '',
    bigchangeJobId: data.bigchange_job_id ?? null,
    bigchangeNoteId: data.bigchange_note_id ?? null,
    statusId: data.status_id ?? null,
    toDo: Boolean(data.to_do),
    inProgress: Boolean(data.in_progress),
    done: Boolean(data.done),
    cancelled: Boolean(data.cancelled),
    isVerified: Boolean(data.is_verified),
    needToBeFollowedUp: Boolean(data.need_to_be_followed_up),
    needToBeFollowedUpSince: data.need_to_be_followed_up_since || null,
    clientInAHurry: Boolean(data.client_in_a_hurry),
    clientInExtremeHurry: Boolean(data.client_in_extreme_hurry),
    clientPhone: data.client_phone || '',
    tags: (data.tags || []).map(interventionTagFromApi),
    projectLabel: data.project_label || '',
    availabilityQuestionnaire:
      data.availability_questionnaire ??
      data.client?.availability_questionnaire ??
      null,
    client,
    openTripDateEligible:
      data.open_trip_date_eligible == null
        ? null
        : Boolean(data.open_trip_date_eligible),
    hasTrip: data.has_trip == null ? null : Boolean(data.has_trip),
    trips: (data.trips || [])
      .map(tripFromApi)
      .filter((trip) => includeImportedTrips || !trip.isImportedFromBigchange),
    unassignedTaskCount: Number(data.unassigned_task_count) || 0,
    workItems: (data.work_items || []).map(workItemFromApi),
  };
}

/** Intervention layer marked as finished (workflow status). */
export function isInterventionLayerDone(layer) {
  return Boolean(layer?.done);
}

/** SAV/VT prestation kept in the database with a cancelled status. */
export function isInterventionLayerCancelled(layer) {
  return Boolean(layer?.cancelled);
}

/** Intervention layer marked as verified by a manager. */
export function isInterventionLayerVerified(layer) {
  return Boolean(layer?.isVerified);
}

/** Project status catalog row (À planifier, En cours…). */
export function clientProjectStatusFromApi(data) {
  if (!data) return null;
  return {
    id: data.id,
    label: data.label || '',
    slug: data.slug || '',
    color: data.color || 'primary',
    sortOrder: data.sort_order ?? 0,
    isDefault: Boolean(data.is_default),
    isClosed: Boolean(data.is_closed),
  };
}

function planningUserFromApi(data) {
  if (!data || data.id == null) return null;
  return {
    id: data.id,
    fullName: data.full_name || '',
    hasAvatar: Boolean(data.has_avatar),
  };
}

/** Project row: the layer between the site and its prestations (1 per signature). */
export function clientProjectFromApi(data) {
  return {
    id: data.id,
    name: data.name || '',
    siteId: data.site_id ?? null,
    clientId: data.client_id ?? null,
    domainId: data.domain_id ?? null,
    serviceTypeId: data.service_type_id ?? null,
    serviceTypeLabel: data.service_type_label || '',
    serviceTypeSlug: data.service_type_slug || '',
    serviceTypeIcon: data.service_type_icon || '',
    serviceTypeColor: data.service_type_color || '',
    status: clientProjectStatusFromApi(data.status),
    statusId: data.status_id ?? data.status?.id ?? null,
    googleFolderId: data.google_folder_id || '',
    googleDriveUrl: data.google_drive_url || '',
    position: data.position ?? 0,
    interventionIds: (data.intervention_ids || []).map(Number),
    signatureProjectManager: planningUserFromApi(
      data.signature_project_manager,
    ),
    projectManager: planningUserFromApi(data.project_manager),
    signatureDate: data.signature_date || null,
    townHallEnabled: Boolean(data.town_hall_enabled),
    townHallAcceptationDate: data.town_hall_acceptation_date || null,
    beginConstructionDate: data.begin_construction_date || null,
    endConstructionDate: data.end_construction_date || null,
    planningStatus: data.planning_status || null,
  };
}

/** A Google Drive file of a site, placed in the site root, a project, or a prestation. */
export function driveFileFromApi(data) {
  return {
    id: data.id,
    siteId: data.site_id ?? null,
    clientId: data.client_id ?? data.client_project_id ?? null,
    interventionId: data.intervention_id ?? null,
    driveFileId: data.drive_file_id || '',
    name: data.name || '',
    mimeType: data.mime_type || '',
    isImage: Boolean(data.is_image),
    position: data.position ?? 0,
    isHidden: Boolean(data.is_hidden),
    thumbnailUrl: data.thumbnail_url || '',
    webViewUrl: data.web_view_url || '',
  };
}

/** Planning project (lead-anchored top level), with chantier sidebar display fields. */
export function projectFromApi(data) {
  const interventionLayers = (data.interventions || []).map((row) =>
    interventionLayerFromApi(row, { includeImportedTrips: true }),
  );
  const clientProjects = [...(data.client_projects || [])]
    .map(clientProjectFromApi)
    .sort((a, b) => Number(b.id) - Number(a.id));
  return {
    id: data.id,
    projectId: data.id,
    clientId: data.client_id ?? null,
    leadId: data.lead_id ?? null,
    leadName: data.lead_name || data.contact_name || data.label || '',
    projectManager: planningUserFromApi(data.project_manager),
    label: data.label || data.lead_name || '',
    ref: data.ref || (data.client_id != null ? String(data.client_id) : ''),
    contact_name: data.contact_name || data.lead_name || data.label || '',
    prenom: data.prenom || '',
    nom_client: data.nom_client || '',
    gender: data.gender ?? null,
    company: data.company || '',
    isCompany: Boolean(data.is_company),
    is_company: Boolean(data.is_company),
    client: data.client || '',
    address: data.address || '',
    dept: data.dept || '',
    city: data.city || '',
    mat: data.mat || '',
    site_id: data.site_id ?? null,
    siteId: data.site_id ?? null,
    google_drive_folder_id: data.google_drive_folder_id ?? null,
    googleDriveFolderId: data.google_drive_folder_id ?? null,
    latitude: data.latitude ?? null,
    longitude: data.longitude ?? null,
    depotTravelMinutes:
      data.depot_travel_minutes != null
        ? Number(data.depot_travel_minutes)
        : null,
    planningSmiley: data.planning_smiley || 'neutral',
    planning_smiley: data.planning_smiley || 'neutral',
    planningSmileyMeta: data.planning_smiley_meta
      ? {
          iconName: data.planning_smiley_meta.icon_name,
          color: data.planning_smiley_meta.color,
          name: data.planning_smiley_meta.name,
        }
      : null,
    planning_smiley_meta: data.planning_smiley_meta ?? null,
    contact_id: data.contact_id ?? null,
    contactId: data.contact_id ?? null,
    pv: Boolean(data.pv),
    bat: Boolean(data.bat),
    aa: Boolean(data.aa),
    ae: Boolean(data.ae),
    ev: Boolean(data.ev),
    hw: Boolean(data.hw),
    th: Boolean(data.th),
    is_verified: Boolean(data.is_verified),
    isClient: Boolean(data.is_client),
    isLead: Boolean(data.is_lead),
    availability_questionnaire: data.availability_questionnaire ?? null,
    interventions: interventionLayers,
    interventionLayers,
    clientProjects,
    incomingTrips: (data.incoming_trips || [])
      .map((row) => ({
        id: row.id,
        date: row.date || '',
        technicianIds: (row.technician_ids || []).map(Number).filter(Boolean),
      }))
      .filter(
        (row) => row.id != null && row.date && row.technicianIds.length > 0,
      ),
  };
}

/** Quick-add catalog: intervention layers with remaining work + project display for map. */
export function quickAddCatalogFromApi(data) {
  const projectsById = new Map();
  const layers = [];
  const trips = [];
  const workItems = [];

  (data.interventions || []).forEach((row) => {
    const layer = interventionLayerFromApi(row);
    layers.push(layer);
    if (row.project?.id != null) {
      projectsById.set(
        row.project.id,
        projectFromApi({
          ...row.project,
          id: row.project.id,
          interventions: [],
        }),
      );
    }
    const items = (
      (row.work_items || []).length
        ? layer.workItems
        : (layer.trips || []).map((trip) => ({
            kind: 'trip',
            id: `trip-${trip.id}`,
            tripId: trip.id,
            interventionId: trip.interventionId ?? layer.id,
            taskIds: [],
            label: trip.label,
            durationMinutes: Math.round((trip.estimatedHours || 0) * 60),
            estimatedHours: trip.estimatedHours,
            minimumTechnicianCount: 1,
            forcedTechnicianIds: [],
            tasks: [],
            groups: trip.groups || [],
            trip,
          }))
    ).filter((item) => !item.trip?.isImportedFromBigchange);
    items.forEach((item) => {
      const mappedTrip =
        item.kind === 'trip'
          ? (layer.trips || []).find(
              (trip) => Number(trip.id) === Number(item.tripId),
            ) || item.trip
          : null;
      const withLayer = {
        ...item,
        interventionLayerId: layer.id,
        chantierId: layer.projectId,
        interventionId: item.interventionId ?? layer.id,
        serviceTypeSlug: layer.serviceTypeSlug,
        ...(mappedTrip
          ? {
              trip: { ...mappedTrip, interventionLayerId: layer.id },
              estimatedHours: mappedTrip.estimatedHours ?? item.estimatedHours,
              groups: mappedTrip.groups || item.groups,
            }
          : {}),
      };
      workItems.push(withLayer);
      if (withLayer.kind === 'trip' && withLayer.trip) {
        trips.push(withLayer.trip);
      }
    });
  });

  return {
    layers,
    trips,
    workItems,
    projects: [...projectsById.values()],
    counts: {
      byServiceType: data.counts?.by_service_type || {},
    },
  };
}

/** Sidebar row: intervention layer + project display (trips nested for grid sync only). */
export function sidebarQueueItemFromApi(data) {
  const projectRaw = data.project || {};
  const projectId = projectRaw.id ?? data.project_id ?? null;
  const project = projectFromApi({
    ...projectRaw,
    id: projectId,
  });
  const intervention = interventionLayerFromApi({
    ...data,
    project_id: projectId,
  });
  return { intervention, project };
}

export function domainFromApi(data) {
  return {
    id: data.id,
    slug: data.slug || '',
    label: data.name || data.label || '',
    name: data.name || data.label || '',
    icon: data.icon || '',
    isActive: data.is_active !== false,
    isProcedureOnly: Boolean(data.is_procedure_only),
  };
}

export function serviceTypeFromApi(data) {
  return {
    id: data.id,
    slug: data.slug || '',
    label: data.name || data.label || '',
    name: data.name || data.label || '',
    icon: data.icon || '',
    color: data.color || 'primary',
    isActive: data.is_active !== false,
    forProcedureOnly: Boolean(data.for_procedure_only),
  };
}

function normalizePlanningTechnicianId(value) {
  if (value == null || value === '') return null;
  const id = Number(value);
  return Number.isNaN(id) ? null : id;
}

/** Week-grid planning slot (explicit field names). */
export function planningSlotFromApi(data) {
  return {
    id: data.id,
    tripId: data.trip_id ?? null,
    interventionLayerId: data.intervention_id ?? null,
    interventionId: data.trip_id ?? data.intervention_id ?? null,
    projectId: data.project_id ?? null,
    afterSalesVisitId: data.after_sales_visit_id ?? null,
    technicalVisitId: data.technical_visit_id ?? null,
    technicianId: normalizePlanningTechnicianId(data.technician_id ?? data.sal),
    date: data.date,
    startTime: data.start_time ?? data.s,
    endTime: data.end_time ?? data.e,
    note: data.note || '',
  };
}

/** Technician absence persisted in the API (date range). */
export function technicianUnavailabilityFromApi(data) {
  const startTime = data.start_time;
  const endTime = data.end_time;
  return {
    id: data.id,
    technicianId: data.technician_id,
    startDate: data.start_date,
    endDate: data.end_date,
    startTime:
      typeof startTime === 'string' && startTime.length >= 5
        ? startTime.slice(0, 5)
        : startTime || '',
    endTime:
      typeof endTime === 'string' && endTime.length >= 5
        ? endTime.slice(0, 5)
        : endTime || '',
    reason: data.reason || '',
    note: data.note || '',
    kind: data.kind || 'unavailability',
    wholeDay: Boolean(data.whole_day),
    companyId: data.company_id ?? null,
    companyName: data.company_name || '',
  };
}

/**
 * Technician busy on another company's trip, shaped like an unavailability
 * (kind ``busy``) so availability checks treat it as a conflict. Read-only.
 */
export function foreignBusyBlockFromApi(data) {
  return {
    id: data.id,
    technicianId: data.technician_id,
    startDate: data.date,
    endDate: data.date,
    startTime: (data.start_time || '').slice(0, 5),
    endTime: (data.end_time || '').slice(0, 5),
    reason: '',
    note: '',
    kind: 'busy',
    wholeDay: false,
    companyId: data.company_id ?? null,
    companyName: data.company_name || '',
    readOnly: true,
  };
}

/** Read-only bundle passed to layout / geo helpers. */
export function buildPlanningSnapshot({
  competences,
  roles,
  taskTemplates,
  technicians,
  contacts,
  sites,
  technicalVisits,
  afterSalesVisits,
  chantiers,
  projects,
  interventions,
  trips,
  planning,
  technicianUnavailabilities,
  technicianGridRows,
  clientDispos,
}) {
  const projectRows = projects ?? chantiers ?? [];
  const tripRows = trips ?? interventions ?? [];
  return {
    competences,
    roles,
    taskTemplates: taskTemplates || [],
    technicians,
    contacts,
    sites,
    technicalVisits,
    afterSalesVisits,
    chantiers: projectRows,
    projects: projectRows,
    interventions: tripRows,
    trips: tripRows,
    planning,
    technicianUnavailabilities,
    technicianGridRows,
    clientDispos,
  };
}
